import { useEffect, useState, useSyncExternalStore } from "react";
import { alreadyThere } from "./reopen.ts";
import { navigate } from "./router.ts";

// Ready without signal (DESIGN.md): the service worker (web/sw/sw.ts) keeps the app, what I last saw
// and the trips' files on this phone. This side registers it, notices when an answer came from what
// the phone kept rather than from Tripmo (and since when), and forgets it all on signing out. And when
// a new version of Tripmo is ready, it says so, and switches to it on Reload.

// How often an open page asks whether Tripmo has a new version (also on opening and coming back).
const CHECK_EVERY_MS = 60 * 60 * 1000;
// How long Reload waits for the new version to take over before loading the page again anyway.
const TAKE_OVER_PATIENCE_MS = 5000;

export function registerWorker() {
  // Only the built app has a worker; in development Vite serves the page.
  if (!import.meta.env.PROD || !("serviceWorker" in navigator)) return;
  const sw = navigator.serviceWorker;
  // The first time, what's on screen loaded before the worker could keep it: once it can, load again,
  // and fetch what every visit starts from (me, Your trips) so it's kept whichever page this is (a trip
  // opened straight away left Your trips unkept, and empty with no signal).
  if (!sw.controller)
    sw.addEventListener(
      "controllerchange",
      () => {
        dispatchEvent(new Event("tripmo:keeping"));
        for (const path of ["/api/me", "/api/trips"]) fetch(path, { credentials: "same-origin" }).catch(() => {});
      },
      { once: true },
    );
  // A notification tapped while Tripmo is open: go where it points (web/sw/sw.ts). One about the trip
  // that's open, at no item or view in it, leaves it where it is.
  sw.addEventListener("message", (event) => {
    const data = event.data as { type?: string; path?: string } | null;
    if (data?.type === "open" && data.path?.startsWith("/") && !alreadyThere(data.path)) navigate(data.path);
  });
  sw.startMessages();
  sw.register("/sw.js")
    .then((registration) => {
      watchForNewVersion(registration);
      // Looks for a new version, then at what it found (a lost answer is asked again here too).
      const check = () => {
        void registration
          .update()
          .catch(() => {})
          .then(look);
      };
      check();
      setInterval(check, CHECK_EVERY_MS);
      document.addEventListener("visibilitychange", () => document.visibilityState === "visible" && check());
    })
    .catch(() => {});
}

// A new version of Tripmo: installed, its files kept whole, and not the version this page runs. Always
// worked out from the worker registration as it is now, never from a worker remembered earlier: a
// release landing while the last one waits replaces it (reported 2026-10-20: the banner was about a
// worker already replaced, and Reload, finding it gone, loaded the page again under the old worker).
let registration: ServiceWorkerRegistration | null = null;
let newVersion = false;
const versionListeners = new Set<() => void>();
// Reload tapped: switching to the newest version, then loading the page again.
let reloading = false;

// The version this page runs, written in by the build (web/vite.config.ts), the same as its worker's.
const running = document.querySelector<HTMLMetaElement>('meta[name="tripmo-version"]')?.content;
// The worker that ran the page when it loaded; another one since means one took over (a Reload in
// another tab), which may be newer than the page.
const startedUnder = "serviceWorker" in navigator ? navigator.serviceWorker.controller : null;

function watchForNewVersion(r: ServiceWorkerRegistration) {
  registration = r;
  r.addEventListener("updatefound", look);
  navigator.serviceWorker.addEventListener("controllerchange", look);
}

// Each worker's every step (installed, replaced, taking over) is a moment to look again.
const watched = new WeakSet<ServiceWorker>();
function watch(worker: ServiceWorker | null) {
  if (!worker || watched.has(worker)) return;
  watched.add(worker);
  worker.addEventListener("statechange", look);
}

// Looks at the registration as it is now: is a version other than this page's ready? On Reload, takes
// the newest over. Asked again on every change, so only the latest look counts.
let looks = 0;
async function look() {
  const r = registration;
  if (!r) return;
  for (const worker of [r.installing, r.waiting, r.active]) watch(worker);
  if (reloading) return takeOver(r);
  const turn = ++looks;
  // A release still installing is the newest, and replaces whatever waits once it's done: until then,
  // what waits may be older than this page (one loaded since the release), so nothing changes, not even
  // by an answer an earlier look is still waiting for.
  if (r.installing) return;
  // What waits, or a worker that took over since the page loaded. Not the worker that ran the page when
  // it loaded: a page comes from the network first, so one loaded after a release runs it while the
  // last version's worker still serves it.
  const controller = navigator.serviceWorker.controller;
  const newest = r.waiting ?? (controller !== startedUnder ? controller : null);
  const version = newest ? await versionOf(newest) : running;
  if (turn !== looks || version === undefined) return;
  setNewVersion(version !== running);
}

function setNewVersion(ready: boolean) {
  if (ready === newVersion) return;
  newVersion = ready;
  for (const l of versionListeners) l();
}

// Which version a worker is: asked (web/sw/sw.ts), since a waiting one runs no page to look at. Only
// an answer counts: a worker slow to answer (started from cold on a busy phone) is no new version until
// it says so, and one that never answers leaves things as they are; the next look asks again. (Until
// 2026-10-20, 10 s of silence read as a new version.)
function versionOf(worker: ServiceWorker): Promise<string | undefined> {
  return new Promise((resolve) => {
    const channel = new MessageChannel();
    channel.port1.onmessage = (event) => resolve(typeof event.data === "string" ? event.data : undefined);
    worker.postMessage({ type: "version" }, [channel.port2]);
  });
}

// Whether a new version is ready.
export function useNewVersion(): boolean {
  return useSyncExternalStore(
    (l) => {
      versionListeners.add(l);
      return () => versionListeners.delete(l);
    },
    () => newVersion,
  );
}

// Reload: the newest version takes over, then the page loads again where it was.
export function reloadToNewVersion() {
  reloading = true;
  void look();
}

// The newest version taking over, one step per look: one still installing first finishes (it replaces
// what waits), what waits is asked to take over, and once nothing waits the page loads again, under the
// newest worker (or straight away, when another tab's Reload already switched).
//
// A worker takes over only once the last one has stopped, which Chromium does once that one has
// nothing in progress, and on a weak signal the last one can wait on the network for minutes (keeping
// what a page asked for, a trip's files): so if it hasn't taken over a few seconds after being asked,
// the page loads again anyway, the new version from the network, and the new worker takes over once
// the last one is done (reported 2026-10-20 by CI, #383 and #366: Reload stayed at "Reloading…"). With
// no signal, loading again would only bring back the page this phone kept: it waits for the take-over,
// or for signal.
const toldToTakeOver = new WeakSet<ServiceWorker>();
let loadingAnyway = false;
function takeOver(r: ServiceWorkerRegistration) {
  if (r.installing) return;
  const waiting = r.waiting;
  if (!waiting) return location.reload();
  if (toldToTakeOver.has(waiting)) return;
  toldToTakeOver.add(waiting);
  waiting.postMessage({ type: "take-over" });
  if (loadingAnyway) return;
  loadingAnyway = true;
  setTimeout(() => {
    if (navigator.onLine) location.reload();
    else addEventListener("online", () => location.reload(), { once: true });
  }, TAKE_OVER_PATIENCE_MS);
}

// Bumps when what's on screen should load again: back online, the worker started keeping things, or
// Tripmo didn't answer while online (retrySoon).
export function useAgain(): number {
  const [again, setAgain] = useState(0);
  useEffect(() => {
    const bump = () => setAgain((n) => n + 1);
    addEventListener("online", bump);
    addEventListener("tripmo:keeping", bump);
    addEventListener("tripmo:again", bump);
    return () => {
      removeEventListener("online", bump);
      removeEventListener("tripmo:keeping", bump);
      removeEventListener("tripmo:again", bump);
    };
  }, []);
  return again;
}

// Online, yet answered from what the phone kept: the browser says it's back a moment before requests
// get through (seen on CI, 2026-10-18: the trip loaded again on "online", failed, and the page said
// "Tripmo isn't answering" for good), or Tripmo is restarting. Load again in a moment, then waiting
// twice as long each time, up to 30 s, until Tripmo answers.
const RETRY_FIRST_MS = 2000;
const RETRY_MAX_MS = 30_000;
let retryWait = RETRY_FIRST_MS;
let retry: ReturnType<typeof setTimeout> | undefined;
function retrySoon() {
  if (retry !== undefined) return;
  retry = setTimeout(() => {
    retry = undefined;
    if (kept.size === 0 || !navigator.onLine) return;
    retryWait = Math.min(retryWait * 2, RETRY_MAX_MS);
    dispatchEvent(new Event("tripmo:again"));
  }, retryWait);
}

// Answers that came from what the phone kept, by address, with when they were kept and why they were used
// (web/sw/sw.ts): Tripmo answered with a server error, or no answer came. An answer from Tripmo itself
// takes its address out again.
const kept = new Map<string, { at: Date; tripmoErred: boolean }>();
const listeners = new Set<() => void>();
export interface Kept {
  // Since when what's on screen was kept: the oldest of it.
  since: Date;
  // Whether Tripmo answered with an error, rather than no answer coming (a weak signal), for any of it.
  tripmoErred: boolean;
}
let snapshot: Kept | null = null;

export function noteAnswer(path: string, keptAt: string | null, because: string | null = null) {
  if (keptAt) {
    kept.set(path, { at: new Date(keptAt), tripmoErred: because === "error" });
    if (navigator.onLine) retrySoon();
  } else if (!kept.delete(path)) return;
  if (kept.size === 0) retryWait = RETRY_FIRST_MS;
  // The oldest of what's on screen: what the page shows is at least that old.
  const all = [...kept.values()];
  const oldest = all.reduce<Date | null>((a, b) => (a && a < b.at ? a : b.at), null);
  const next = oldest && { since: oldest, tripmoErred: all.some((k) => k.tripmoErred) };
  if (next?.since.getTime() === snapshot?.since.getTime() && next?.tripmoErred === snapshot?.tripmoErred) return;
  snapshot = next;
  for (const l of listeners) l();
}

// Back online, the page loads again (useAgain): what was kept is no longer what's on screen.
addEventListener("online", () => {
  kept.clear();
  snapshot = null;
  for (const l of listeners) l();
});

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

// Since when what's on screen was kept, and why, while some of it came from the phone; else null.
export function useKept(): Kept | null {
  return useSyncExternalStore(subscribe, () => snapshot);
}

// Whether the browser thinks it has a connection (it can be wrong the other way: a weak signal).
export function useOnline(): boolean {
  const [online, setOnline] = useState(() => navigator.onLine);
  useEffect(() => {
    const update = () => setOnline(navigator.onLine);
    addEventListener("online", update);
    addEventListener("offline", update);
    return () => {
      removeEventListener("online", update);
      removeEventListener("offline", update);
    };
  }, []);
  return online;
}

// Signing out: what this phone kept of me goes (the app itself stays).
export async function forgetKept() {
  if (!("caches" in globalThis)) return;
  await Promise.all([caches.delete("tripmo-data"), caches.delete("tripmo-files")]).catch(() => {});
}

// Installing. Android's browsers offer it as an event the page can act on later; iPhones never do, so
// the page says how. Whether it's already installed: the app runs in its own window.
type InstallPrompt = Event & { prompt: () => Promise<void> };
let installPrompt: InstallPrompt | null = null;
const installListeners = new Set<() => void>();

export function listenForInstall() {
  addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    installPrompt = e as InstallPrompt;
    for (const l of installListeners) l();
  });
  addEventListener("appinstalled", () => {
    installPrompt = null;
    for (const l of installListeners) l();
  });
}

export function useInstallPrompt(): InstallPrompt | null {
  return useSyncExternalStore(
    (l) => {
      installListeners.add(l);
      return () => installListeners.delete(l);
    },
    () => installPrompt,
  );
}

export const installed = () =>
  matchMedia("(display-mode: standalone)").matches ||
  (navigator as Navigator & { standalone?: boolean }).standalone === true;

export const isIPhone = () =>
  /iPhone|iPad|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

// "14:02" today, else "Tue 13 Oct, 14:02".
export function keptWhen(at: Date, now = new Date()): string {
  const time = at.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  if (at.toDateString() === now.toDateString()) return time;
  const day = at.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
  return `${day}, ${time}`;
}
